<template>
  <div class="search-input">
    <label v-if="label" :for="inputId" class="form-label">{{ label }}</label>
    <div class="search-input-wrapper">
      <input
        :id="inputId"
        :value="searchTerm"
        type="text"
        class="form-control"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @input="handleInput"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
      />
      
      <button
        v-if="showClearButton"
        type="button"
        class="clear-button"
        :disabled="loading"
        :title="loading ? 'Recherche en cours…' : clearButtonTitle"
        @click="clearInput"
      >
        <span v-if="loading" class="btn-spinner" />
        <span v-else>✕</span>
      </button>
    </div>

    <SuggestionsList
      :suggestions="suggestions"
      :key-field="suggestionKeyField"
      @select="handleSuggestionSelect"
    >
      <template #default="{ suggestion }">
        <slot name="suggestion" :suggestion="suggestion">
          {{ suggestion.firstname }} {{ suggestion.lastname }}
          <small v-if="suggestion.email"> — {{ suggestion.email }}</small>
        </slot>
      </template>
    </SuggestionsList>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import SuggestionsList from './SuggestionsList.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  suggestions: {
    type: Array,
    default: () => []
  },
  suggestionKeyField: {
    type: String,
    default: 'id'
  },
  hasValue: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  clearButtonTitle: {
    type: String,
    default: 'Effacer'
  }
})

const emit = defineEmits([
  'update:modelValue',
  'search',
  'select',
  'clear',
  'clear-suggestions',
  'focus',
  'blur'
])

const searchTerm = ref(props.modelValue)
const inputId = computed(() => `search-input-${Math.random().toString(36).substr(2, 9)}`)

const showClearButton = computed(() => {
  return props.hasValue || searchTerm.value
})

watch(() => props.modelValue, (newValue) => {
  searchTerm.value = newValue
})

const handleInput = (e) => {
  const value = e.target.value
  searchTerm.value = value
  emit('update:modelValue', value)
  emit('search', value)
}

const clearInput = () => {
  searchTerm.value = ''
  emit('update:modelValue', '')
  emit('clear')
}

const handleSuggestionSelect = (suggestion) => {
  emit('select', suggestion)
}
</script>

<style scoped>
.search-input {
  position: relative;
}

.search-input-wrapper {
  position: relative;
}


.clear-button:hover {
  color: var(--danger);
}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #dee2e6;
  border-top-color: var(--primary, #0d6efd);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.form-text {
  display: block;
  margin-top: 6px;
  color: #6c757d;
  font-size: 0.875rem;
}
</style>
